/**
 * API Client for LMS BID V3
 *
 * This package provides a typed API client for communicating
 * with the LMS BID V3 backend.
 *
 * @example
 * ```typescript
 * import { createApiClient } from '@lms/api-client';
 *
 * const client = createApiClient({
 *   baseUrl: 'http://localhost:3001',
 *   token: 'your-auth-token'
 * });
 *
 * const programs = await client.programs.list();
 * ```
 */

import type {
  AcademicTranscript,
  ComprehensiveTranscript,
  LegacyAcademicTranscriptResponse,
  ProgramCohortReport,
  PaginationMeta,
  User,
  Role,
  Program,
  ProgramReport,
  ProgramLevel,
  ProgramStage,
  ProgramSubject,
  LessonSession,
  LearningFlow,
  ProgramRegistration,
  Enrollment,
  EnrollmentStatus,
  Material,
  Evaluation,
  EvaluationQuestion,
  EvaluationAttempt,
  QuestionBankQuestion,
  QuestionBankListResponse,
  MaterialCompletion,
  Reflection,
  LearningResult,
  WhatsAppGroup,
  WhatsAppGroupMember,
  ReflectionStatus,
  RegistrationStatus,
  ParticipantId,
  RegistrationReceipt,
  MaterialMedia,
  Announcement,
  AnnouncementCategory,
  AnnouncementPriority,
  AnnouncementTarget,
  CreateAnnouncementInput,
  UpdateAnnouncementInput,
  CrmParticipantsResponse,
  ParticipantOutreachLog,
  CreateOutreachLogInput,
  QuickWhatsAppInput,
  SendReminderEmailInput,
  OutreachStatus,
  OutreachChannel,
  OutreachType,
  CrmParticipantItem,
  CrmMetricsSummary,
} from '@lms/shared/types';

export interface ApiClientConfig {
  baseUrl: string;
  token?: string;
  headers?: Record<string, string>;
  onError?: (error: ApiError) => void;
  /**
   * Called when a 401 is received AND no refresh succeeded. Receives the
   * raw error message from the API response body (e.g. 'Akun tidak aktif',
   * 'Token tidak valid') so the host app can surface a user-friendly
   * explanation (e.g. via sessionStorage → login banner). Falls back to
   * the synthetic 'Token tidak valid' if the body could not be parsed.
   */
  on401?: (reason?: string) => void;
  /**
   * Optional session-renewal hook. Called once when a 401 is received and a
   * renewal is not already in flight. It should obtain a new access token
   * from the host auth provider and call `applyToken`, or `applyToken(null)`
   * if renewal failed (which triggers the `on401` logout flow).
   *
   * The api-client deliberately knows nothing about localStorage or your
   * auth store — the host app provides this hook so it can persist the
   * new tokens however it likes.
   */
  refreshHandler?: (applyToken: (token: string | null) => void) => void;
}

export type EnrollmentListItem = Enrollment & {
  user: Pick<User, 'id' | 'email' | 'name'>;
  program: Pick<Program, 'id' | 'code' | 'name' | 'coverImage' | 'status'>;
};

export interface PaginatedEnrollmentResponse {
  items: EnrollmentListItem[];
  meta: PaginationMeta;
  summary: {
    averageProgress: number;
    statusCounts: Record<EnrollmentStatus, number>;
  };
}

export interface PaginatedMaterialResponse {
  items: Material[];
  meta: PaginationMeta;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  params?: Record<string, string | number | boolean | undefined>;
}

class ApiError extends Error {
  public code: string;
  public details?: Record<string, string[]>;
  /**
   * HTTP status code from the response (e.g. 409, 404). Useful for
   * callers that want to branch on status without parsing `code`.
   */
  public statusCode?: number;
  /**
   * Raw decoded response body. Lets callers read backend-specific
   * fields that aren't part of the standard error envelope (e.g. the
   * 409 "Incomplete User Profile" response carries `missingFields`,
   * `userId`, and `registrationId` so the admin UI can auto-open an
   * edit-user dialog prefilled with the missing fields).
   */
  public body?: unknown;

  constructor(
    code: string,
    message: string,
    details?: Record<string, string[]>,
    options?: { statusCode?: number; body?: unknown }
  ) {
    super(message);
    this.code = code;
    this.details = details;
    this.statusCode = options?.statusCode;
    this.body = options?.body;
    this.name = 'ApiError';
  }
}

export class ApiClient {
  private config: ApiClientConfig;

  // Single in-flight refresh promise — all concurrent 401s share it.
  private refreshInFlight: Promise<boolean> | null = null;

  constructor(config: ApiClientConfig) {
    this.config = config;
  }

  public setToken(token: string | null) {
    this.config.token = token || undefined;
  }

  /**
   * Decodes a JWT's `exp` claim (base64url payload, no dependencies) to
   * decide whether the token is close to expiry. Returns false for
   * non-JWT tokens or malformed payloads so callers always fall through
   * to the normal request flow.
   */
  private isTokenExpiringSoon(token: string, withinMs = 5 * 60 * 1000): boolean {
    try {
      const payload = token.split('.')[1];
      if (!payload) {
        return false;
      }
      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
      const data = JSON.parse(atob(normalized)) as { exp?: number };
      if (typeof data.exp !== 'number') {
        return false;
      }
      return data.exp * 1000 < Date.now() + withinMs;
    } catch {
      return false;
    }
  }

  /**
   * Runs the refreshHandler exactly once (deduplicated across concurrent
   * callers) and applies the fresh token to the client config on success.
   * Returns true when a new token was applied. Unlike `handle401`, this
   * does NOT fire `on401` on failure — pre-flight renewal must leave logout
   * to the real 401 flow so a transient refresh failure doesn't kick an
   * otherwise-valid session to the login page early.
   */
  private async refreshToken(): Promise<boolean> {
    if (!this.config.refreshHandler) {
      return false;
    }

    if (!this.refreshInFlight) {
      this.refreshInFlight = new Promise<boolean>(resolve => {
        this.config.refreshHandler!(token => {
          if (token) {
            this.config.token = token;
          }
          resolve(token !== null);
        });
      }).finally(() => {
        this.refreshInFlight = null;
      });
    }

    return this.refreshInFlight;
  }

  private async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const { method = 'GET', body, headers = {}, params } = options;

    // Pre-flight: Neon Auth access tokens are short-lived (~15 min). If the
    // token is near expiry, renew BEFORE firing so we don't produce a doomed
    // request that 401s and needs a retry — and, at app boot with a stale
    // localStorage token, a whole parallel burst of doomed requests. A failed
    // renewal is ignored here; the 401 flow still owns the logout.
    if (
      this.config.refreshHandler &&
      this.config.token &&
      this.isTokenExpiringSoon(this.config.token)
    ) {
      await this.refreshToken();
    }

    // Build URL with query params
    const url = new URL(endpoint, this.config.baseUrl);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          url.searchParams.set(key, String(value));
        }
      });
    }

    // Build headers
    const requestHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      ...this.config.headers,
      ...headers,
    };

    if (this.config.token) {
      requestHeaders['Authorization'] = `Bearer ${this.config.token}`;
    }

    // Make request
    const response = await fetch(url.toString(), {
      method,
      headers: requestHeaders,
      body: body ? JSON.stringify(body) : undefined,
    });

    if (response.status === 401) {
      // Best-effort: capture the original 401 reason from the response body
      // so the host app can show a user-friendly explanation (e.g. account
      // deactivated vs session expired). The body is consumed lazily — we
      // `clone()` because fetch() can only read the body once.
      let reason: string | undefined;
      try {
        const raw = await response.clone().json();
        const data = raw as {
          error?: { message?: string };
          message?: string | string[];
        };
        reason =
          data.error?.message ?? (Array.isArray(data.message) ? data.message[0] : data.message);
      } catch {
        // Non-JSON body or empty — fall through with undefined.
      }

      const retried = await this.handle401(endpoint, options);
      if (retried) {
        return this.request<T>(endpoint, options);
      }
      // Refresh failed or unavailable — fire the legacy logout hook with
      // whatever reason we managed to extract.
      this.config.on401?.(reason);
      // Throw a synthetic ApiError so callers still get a typed error.
      throw new ApiError('UNAUTHORIZED', reason ?? 'Token tidak valid');
    }

    if (response.status === 204) {
      return {} as T;
    }

    // Safely parse JSON
    let data: unknown;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      try {
        data = await response.json();
      } catch {
        data = { message: 'Failed to parse JSON response' };
      }
    } else {
      data = { message: await response.text() };
    }

    if (!response.ok) {
      const errorData = data as {
        error?: { code?: string; message?: string; details?: Record<string, string[]> } | string;
        statusCode?: number;
        message?: string | string[];
      };
      // `error` may either be the typed envelope `{ code, message, details }`
      // or — as in NestJS's default exception filter — a plain string
      // like "Incomplete User Profile". Handle both shapes when
      // extracting code/message.
      const errorObj =
        typeof errorData.error === 'object' && errorData.error !== null
          ? errorData.error
          : undefined;
      const errorString = typeof errorData.error === 'string' ? errorData.error : undefined;
      const apiError = new ApiError(
        errorObj?.code ?? errorData.statusCode?.toString() ?? 'UNKNOWN_ERROR',
        errorObj?.message ??
          errorString ??
          (Array.isArray(errorData.message) ? errorData.message.join(', ') : errorData.message) ??
          'An unknown error occurred',
        errorObj?.details,
        { statusCode: response.status, body: data }
      );
      this.config.onError?.(apiError);
      throw apiError;
    }

    // Check if the response follows the { success: true, data: ... } format
    if (data && typeof data === 'object' && 'success' in data) {
      const result = data as {
        success: boolean;
        data?: T;
        error?: { code?: string; message?: string; details?: Record<string, string[]> };
      };
      if (!result.success) {
        const apiError = new ApiError(
          result.error?.code ?? 'UNKNOWN_ERROR',
          result.error?.message ?? 'An unknown error occurred',
          result.error?.details
        );
        this.config.onError?.(apiError);
        throw apiError;
      }
      return (result.data !== undefined ? result.data : result) as T;
    }

    // Otherwise, assume the payload is the data itself (raw NestJS response)
    return data as T;
  }

  /**
   * Centralised 401 handler. If a refreshHandler is configured, attempts
   * exactly one session renewal (deduplicated across concurrent 401s) and
   * returns true on success so the caller can retry the original request.
   * Returns false if no refreshHandler is configured or if renewal fails.
   */
  private async handle401(_endpoint: string, _options: RequestOptions): Promise<boolean> {
    if (!this.config.refreshHandler) {
      return false;
    }

    const success = await this.refreshToken();
    if (!success) {
      // Session renewal failed — treat as a session-expired logout. The
      // original 401 reason (if any) will be passed by the caller in
      // request() above.
      this.config.on401?.('Sesi tidak valid');
    }
    return success;
  }

  private async upload<T>(
    endpoint: string,
    formData: FormData,
    headers: Record<string, string> = {}
  ): Promise<T> {
    const url = new URL(endpoint, this.config.baseUrl);

    const requestHeaders: Record<string, string> = {
      ...this.config.headers,
      ...headers,
    };

    if (this.config.token) {
      requestHeaders['Authorization'] = `Bearer ${this.config.token}`;
    }

    // When uploading FormData, Fetch API sets the Content-Type automatically with boundary
    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: requestHeaders,
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      const apiError = new ApiError(
        data.statusCode?.toString() || 'UPLOAD_ERROR',
        data.message || 'Failed to upload file'
      );
      this.config.onError?.(apiError);
      throw apiError;
    }

    return data as T;
  }

  // Auth endpoints
  auth = {
    verifyLegacyCredentials: (data: { identifier: string; password: string }) =>
      this.request<{ email: string; name: string }>('/api/v1/auth/legacy-credentials', {
        method: 'POST',
        body: data,
      }),

    syncProfile: (data: { name: string; phone?: string; gender: 'MALE' | 'FEMALE' }) =>
      this.request<User>('/api/v1/auth/sync-profile', {
        method: 'POST',
        body: data,
      }),

    me: () => this.request<User>('/api/v1/auth/me'),

    updateProfile: (data: Partial<User>) =>
      this.request<User>('/api/v1/auth/me', { method: 'PUT', body: data }),
  };

  // Program endpoints
  programs = {
    getOverview: () =>
      this.request<{
        stats: {
          totalParticipants: number;
          activeParticipants: number;
          activeMaterials: number;
          completionRate: number;
          completedFinalEvaluations: number;
          incompleteParticipants: number;
          totalRegistrations: number;
          pendingRegistrations: number;
          approvedRegistrations: number;
          pendingToday: number;
          activeLast7Days: number;
          incompleteLast7Days: number;
          pendingReflections?: number;
        };
        chartData: {
          id?: string;
          code?: string;
          name: string;
          fullName?: string;
          coverImage?: string | null;
          status?: string;
          description?: string | null;
          participants: number;
          completion: number;
        }[];
        activities: {
          id: string;
          type: string;
          description: string;
          actor: string;
          timestamp: string;
        }[];
      }>('/api/v1/programs/reports/overview'),

    getReport: (id: string) => this.request<ProgramCohortReport>(`/api/v1/programs/${id}/reports`),

    exportReport: (id: string) => this.request<Blob>(`/api/v1/programs/${id}/reports/export`),

    list: (params?: { page?: number; limit?: number; status?: string }) =>
      this.request<{ items: Program[]; meta: PaginationMeta }>('/api/v1/programs', { params }),

    get: (id: string) =>
      this.request<Program & { levels: ProgramLevel[] }>(`/api/v1/programs/${id}`),

    getRegistrationLink: (reference: string) =>
      this.request<
        Pick<
          Program,
          | 'id'
          | 'code'
          | 'name'
          | 'description'
          | 'coverImage'
          | 'status'
          | 'approvalMode'
          | 'isRegistrationOpen'
          | 'registrationOpensAt'
          | 'registrationClosesAt'
          | 'startDate'
          | 'endDate'
        >
      >(`/api/v1/programs/registration-link/${encodeURIComponent(reference)}`),

    create: (data: Partial<Program>) =>
      this.request<Program>('/api/v1/programs', { method: 'POST', body: data }),

    update: (id: string, data: Partial<Program>) =>
      this.request<Program>(`/api/v1/programs/${id}`, { method: 'PUT', body: data }),

    duplicate: (id: string, data: Pick<Program, 'code' | 'name'>) =>
      this.request<Program>(`/api/v1/programs/${id}/duplicate`, {
        method: 'POST',
        body: data,
      }),

    delete: (id: string) => this.request<void>(`/api/v1/programs/${id}`, { method: 'DELETE' }),

    sync: (id: string) => this.request<void>(`/api/v1/programs/${id}/sync`, { method: 'POST' }),

    getLevels: (id: string) => this.request<ProgramLevel[]>(`/api/v1/programs/${id}/levels`),

    createLevel: (programId: string, data: Partial<ProgramLevel>) =>
      this.request<ProgramLevel>(`/api/v1/programs/${programId}/levels`, {
        method: 'POST',
        body: data,
      }),

    updateLevel: (programId: string, levelId: string, data: Partial<ProgramLevel>) =>
      this.request<ProgramLevel>(`/api/v1/programs/${programId}/levels/${levelId}`, {
        method: 'PUT',
        body: data,
      }),

    deleteLevel: (programId: string, levelId: string) =>
      this.request<void>(`/api/v1/programs/${programId}/levels/${levelId}`, {
        method: 'DELETE',
      }),
  };

  // Learning Flow endpoints
  learningFlows = {
    list: (programId: string, levelId?: string) =>
      this.request<LearningFlow[]>(`/api/v1/learning-flows`, { params: { programId, levelId } }),

    get: (id: string) => this.request<LearningFlow>(`/api/v1/learning-flows/${id}`),

    create: (data: Partial<LearningFlow>) =>
      this.request<LearningFlow>('/api/v1/learning-flows', { method: 'POST', body: data }),

    update: (id: string, data: Partial<LearningFlow>) =>
      this.request<LearningFlow>(`/api/v1/learning-flows/${id}`, { method: 'PUT', body: data }),

    delete: (id: string) =>
      this.request<void>(`/api/v1/learning-flows/${id}`, { method: 'DELETE' }),

    reorder: (reorderDtos: { id: string; order: number }[]) =>
      this.request<void>('/api/v1/learning-flows/reorder', { method: 'PATCH', body: reorderDtos }),
  };

  // Program Stage endpoints (Tahapan / Tema Pelajaran)
  programStages = {
    list: (levelId: string) =>
      this.request<ProgramStage[]>('/api/v1/program-stages', { params: { levelId } }),

    get: (id: string) => this.request<ProgramStage>(`/api/v1/program-stages/${id}`),

    create: (data: Partial<ProgramStage> & { levelId: string; name: string }) =>
      this.request<ProgramStage>('/api/v1/program-stages', { method: 'POST', body: data }),

    update: (id: string, data: Partial<ProgramStage>) =>
      this.request<ProgramStage>(`/api/v1/program-stages/${id}`, { method: 'PUT', body: data }),

    delete: (id: string) =>
      this.request<void>(`/api/v1/program-stages/${id}`, { method: 'DELETE' }),

    reorder: (items: { id: string; order: number }[]) =>
      this.request<void>('/api/v1/program-stages/reorder', { method: 'PUT', body: { items } }),
  };

  // Program Subject endpoints (Mata Pelajaran)
  programSubjects = {
    list: (stageId: string) =>
      this.request<ProgramSubject[]>('/api/v1/program-subjects', { params: { stageId } }),

    get: (id: string) => this.request<ProgramSubject>(`/api/v1/program-subjects/${id}`),

    create: (data: Partial<ProgramSubject> & { stageId: string; name: string }) =>
      this.request<ProgramSubject>('/api/v1/program-subjects', { method: 'POST', body: data }),

    update: (id: string, data: Partial<ProgramSubject>) =>
      this.request<ProgramSubject>(`/api/v1/program-subjects/${id}`, { method: 'PUT', body: data }),

    delete: (id: string) =>
      this.request<void>(`/api/v1/program-subjects/${id}`, { method: 'DELETE' }),

    reorder: (items: { id: string; order: number }[]) =>
      this.request<void>('/api/v1/program-subjects/reorder', { method: 'PUT', body: { items } }),
  };

  // Lesson Session endpoints (Pertemuan)
  lessonSessions = {
    list: (subjectId: string) =>
      this.request<LessonSession[]>('/api/v1/lesson-sessions', { params: { subjectId } }),

    get: (id: string) => this.request<LessonSession>(`/api/v1/lesson-sessions/${id}`),

    create: (
      data: Partial<LessonSession> & { subjectId: string; sessionNumber: number; title: string }
    ) => this.request<LessonSession>('/api/v1/lesson-sessions', { method: 'POST', body: data }),

    bulkCreate: (data: { subjectId: string; count: number; titlePrefix?: string }) =>
      this.request<LessonSession[]>('/api/v1/lesson-sessions/bulk', { method: 'POST', body: data }),

    update: (id: string, data: Partial<LessonSession>) =>
      this.request<LessonSession>(`/api/v1/lesson-sessions/${id}`, { method: 'PUT', body: data }),

    delete: (id: string) =>
      this.request<void>(`/api/v1/lesson-sessions/${id}`, { method: 'DELETE' }),

    reorder: (items: { id: string; sessionNumber: number }[]) =>
      this.request<void>('/api/v1/lesson-sessions/reorder', { method: 'PUT', body: { items } }),
  };

  // Registration endpoints
  registrations = {
    list: (params?: {
      page?: number;
      limit?: number;
      status?: RegistrationStatus;
      programId?: string;
      /**
       * Set to `true` to restrict the result to registrations that have
       * been re-submitted at least once (i.e. the user re-submitted
       * after their registration was REJECTED / REVISION / CANCELLED).
       * Backed by the `lastResubmittedAt` column.
       */
      resubmitted?: boolean;
    }) =>
      this.request<{ items: ProgramRegistration[]; meta: PaginationMeta }>(
        '/api/v1/registrations',
        { params }
      ),

    get: (id: string) =>
      this.request<
        ProgramRegistration & {
          user: User;
          program: Program;
          participantId: ParticipantId;
          receipts: RegistrationReceipt[];
        }
      >(`/api/v1/registrations/${id}`),

    create: (data: {
      programId: string;
      // Backend CreateRegistrationDto marks email + name as required
      // (used to find-or-create the User row). Callers must pass both.
      email: string;
      name: string;
      phone?: string;
      gender?: 'MALE' | 'FEMALE';
      notes?: string;
      consentGiven: boolean;
      domisili: string;
      reason: string;
    }) =>
      this.request<ProgramRegistration>('/api/v1/registrations', { method: 'POST', body: data }),

    approve: (id: string, adminNotes?: string) =>
      this.request<ProgramRegistration>(`/api/v1/registrations/${id}/approve`, {
        method: 'POST',
        body: { adminNotes },
      }),

    reject: (id: string, adminNotes: string) =>
      this.request<ProgramRegistration>(`/api/v1/registrations/${id}/reject`, {
        method: 'POST',
        body: { adminNotes },
      }),

    requestRevision: (id: string, revisionNotes: string) =>
      this.request<ProgramRegistration>(`/api/v1/registrations/${id}/request-revision`, {
        method: 'POST',
        body: { revisionNotes },
      }),

    assignWhatsAppGroup: (id: string, whatsappGroupId: string) =>
      this.request<ProgramRegistration>(`/api/v1/registrations/${id}/assign-whatsapp-group`, {
        method: 'POST',
        body: { whatsappGroupId },
      }),

    getByUser: (userId: string) =>
      this.request<{ items: ProgramRegistration[]; meta: PaginationMeta }>(
        `/api/v1/registrations/user/${userId}`
      ),
  };

  // Enrollment endpoints
  enrollments = {
    list: (params?: { userId?: string; programId?: string }) =>
      this.request<
        (Enrollment & {
          program: Program & {
            flows: (LearningFlow & { materials: Material[] })[];
            evaluations: Evaluation[];
          };
          completions: MaterialCompletion[];
          results: LearningResult[];
        })[]
      >('/api/v1/enrollments', { params }),

    listPage: (params: {
      page: number;
      limit?: number;
      userId?: string;
      programId?: string;
      search?: string;
      status?: EnrollmentStatus;
    }) =>
      this.request<PaginatedEnrollmentResponse>('/api/v1/enrollments', {
        params,
      }),

    get: (id: string) =>
      this.request<
        Enrollment & {
          program: Program;
          completions: MaterialCompletion[];
          results: LearningResult[];
        }
      >(`/api/v1/enrollments/${id}`),

    getProgress: (id: string) =>
      this.request<{
        progressPercent: number;
        completedCount: number;
        totalRequired: number;
        totalMaterials: number;
      }>(`/api/v1/enrollments/${id}/progress`),
  };

  // Material endpoints
  materials = {
    list: (params?: { flowId?: string; type?: string }) =>
      this.request<Material[]>(`/api/v1/materials`, { params }),

    listPage: (params: {
      flowId?: string;
      type?: string;
      page: number;
      limit: number;
      search?: string;
    }) => this.request<PaginatedMaterialResponse>(`/api/v1/materials`, { params }),

    get: (id: string, enrollmentId?: string) =>
      this.request<Material & { evaluations: Evaluation[] }>(`/api/v1/materials/${id}`, {
        params: enrollmentId ? { enrollmentId } : undefined,
      }),

    create: (data: Partial<Material>) =>
      this.request<Material>('/api/v1/materials', { method: 'POST', body: data }),

    update: (id: string, data: Partial<Material>) =>
      this.request<Material>(`/api/v1/materials/${id}`, { method: 'PUT', body: data }),

    delete: (id: string) => this.request<void>(`/api/v1/materials/${id}`, { method: 'DELETE' }),

    complete: (id: string, data: { enrollmentId: string; timeSpent?: number }) =>
      this.request<MaterialCompletion>(`/api/v1/materials/${id}/complete`, {
        method: 'POST',
        body: data,
      }),

    reorder: (data: { flowId: string; materialIds: string[] }) =>
      this.request<void>(`/api/v1/materials/reorder`, {
        method: 'POST',
        body: data,
      }),

    getMedia: (materialId: string) =>
      this.request<MaterialMedia[]>(`/api/v1/materials/media`, { params: { materialId } }),

    addMedia: (data: {
      materialId: string;
      url: string;
      type: string;
      filename?: string;
      size?: number;
      mimeType?: string;
    }) => this.request<MaterialMedia>(`/api/v1/materials/media`, { method: 'POST', body: data }),

    deleteMedia: (id: string) =>
      this.request<void>(`/api/v1/materials/media/${id}`, { method: 'DELETE' }),

    importMaterial: (data: { sourceMaterialId: string; targetFlowId: string }) =>
      this.request<Material>(`/api/v1/materials/import`, {
        method: 'POST',
        body: data,
      }),
  };

  // Material Media endpoints
  materialMedia = {
    list: (materialId: string) =>
      this.request<MaterialMedia[]>(`/api/v1/materials/media`, { params: { materialId } }),

    create: (data: {
      materialId: string;
      url: string;
      type: string;
      filename?: string;
      size?: number;
      mimeType?: string;
    }) => this.request<MaterialMedia>(`/api/v1/materials/media`, { method: 'POST', body: data }),

    delete: (id: string) =>
      this.request<void>(`/api/v1/materials/media/${id}`, { method: 'DELETE' }),
  };

  // Evaluation endpoints
  evaluations = {
    list: (params?: {
      materialId?: string;
      flowId?: string;
      levelId?: string;
      programId?: string;
      userId?: string;
    }) => this.request<Evaluation[]>(`/api/v1/evaluations`, { params }),

    get: (id: string, userId?: string) =>
      this.request<
        Evaluation & { questions: EvaluationQuestion[]; attempts?: EvaluationAttempt[] }
      >(`/api/v1/evaluations/${id}`, { params: { userId } }),

    create: (data: Partial<Evaluation>) =>
      this.request<Evaluation>('/api/v1/evaluations', { method: 'POST', body: data }),

    update: (id: string, data: Partial<Evaluation>) =>
      this.request<Evaluation>(`/api/v1/evaluations/${id}`, { method: 'PUT', body: data }),

    delete: (id: string) => this.request<void>(`/api/v1/evaluations/${id}`, { method: 'DELETE' }),

    addQuestion: (evaluationId: string, data: Partial<EvaluationQuestion>) =>
      this.request<EvaluationQuestion>(`/api/v1/evaluations/${evaluationId}/questions`, {
        method: 'POST',
        body: data,
      }),

    updateQuestion: (id: string, data: Partial<EvaluationQuestion>) =>
      this.request<EvaluationQuestion>(`/api/v1/evaluations/questions/${id}`, {
        method: 'PUT',
        body: data,
      }),

    bulkAddQuestions: (
      id: string,
      data: {
        questions: Array<{
          question: string;
          type: string;
          // Optional here so callers that already carry a UI-level
          // `order` (e.g. ParsedQuestion[]) don't trip TS2339 at the
          // defensive strip below. The server's BulkCreateQuestionDto
          // (PR #112) deliberately omits `order` because it assigns
          // `currentMax + 1..N` server-side; with
          // `forbidNonWhitelisted: true` on the global ValidationPipe
          // any caller forwarding `order` here 400s.
          order?: number;
          explanation?: string;
          points?: number;
          options?: { text: string; isCorrect: boolean; order: number }[];
        }>;
      }
    ) =>
      // Defense-in-depth strip: every consumer (web-admin hook,
      // participant app, scripts, future tooling) is protected without
      // each one having to remember to shape-strip. Per-option `order`
      // is preserved because CreateQuestionOptionDto still requires it.
      this.request<EvaluationQuestion[]>(`/api/v1/evaluations/${id}/bulk-questions`, {
        method: 'POST',
        body: {
          questions: data.questions.map(q => {
            const { order: _strip, ...rest } = q;
            return rest;
          }),
        },
      }),

    deleteQuestion: (id: string) =>
      this.request<void>(`/api/v1/evaluations/questions/${id}`, { method: 'DELETE' }),

    importQuestions: (id: string, data: { sourceEvaluationId: string; questionIds?: string[] }) =>
      this.request<EvaluationQuestion[]>(`/api/v1/evaluations/${id}/import-questions`, {
        method: 'POST',
        body: data,
      }),

    importBankQuestions: (id: string, questionIds: string[]) =>
      this.request<EvaluationQuestion[]>(`/api/v1/evaluations/${id}/import-bank-questions`, {
        method: 'POST',
        body: { questionIds },
      }),

    startAttempt: (evaluationId: string, userId: string) =>
      this.request<EvaluationAttempt>(`/api/v1/evaluations/${evaluationId}/attempts`, {
        method: 'POST',
        body: { userId },
      }),

    submitAttempt: (
      attemptId: string,
      answers: { questionId: string; optionId?: string; textAnswer?: string }[]
    ) =>
      this.request<{ score: number; isPassed: boolean }>(
        `/api/v1/evaluations/attempts/${attemptId}/submit`,
        { method: 'POST', body: { answers } }
      ),

    resetAttempts: (evaluationId: string, userId: string) =>
      this.request<void>(`/api/v1/evaluations/${evaluationId}/attempts/reset`, {
        method: 'POST',
        body: { userId },
      }),
  };

  questionBank = {
    list: (params?: {
      programId?: string;
      subjectId?: string;
      type?: string;
      search?: string;
      page?: number;
      limit?: number;
    }) => this.request<QuestionBankListResponse>('/api/v1/question-bank', { params }),

    get: (id: string) => this.request<QuestionBankQuestion>(`/api/v1/question-bank/${id}`),

    create: (
      data: Omit<QuestionBankQuestion, 'id' | 'options' | 'createdAt' | 'updatedAt'> & {
        options?: Array<Omit<QuestionBankQuestion['options'][number], 'id' | 'bankQuestionId'>>;
      }
    ) =>
      this.request<QuestionBankQuestion>('/api/v1/question-bank', { method: 'POST', body: data }),

    update: (
      id: string,
      data: Partial<Omit<QuestionBankQuestion, 'id' | 'options' | 'createdAt' | 'updatedAt'>> & {
        options?: Array<Omit<QuestionBankQuestion['options'][number], 'id' | 'bankQuestionId'>>;
      }
    ) =>
      this.request<QuestionBankQuestion>(`/api/v1/question-bank/${id}`, {
        method: 'PUT',
        body: data,
      }),

    delete: (id: string) => this.request<void>(`/api/v1/question-bank/${id}`, { method: 'DELETE' }),

    bulkCreate: (data: {
      programId: string;
      subjectId?: string;
      questions: Array<{
        question: string;
        type: string;
        explanation?: string;
        points?: number;
        options?: { text: string; isCorrect: boolean; order: number }[];
      }>;
    }) =>
      this.request<{ imported: number }>('/api/v1/question-bank/bulk', {
        method: 'POST',
        body: data,
      }),

    copy: (data: { targetProgramId: string; targetSubjectId?: string; questionIds: string[] }) =>
      this.request<{ imported: number }>('/api/v1/question-bank/copy', {
        method: 'POST',
        body: data,
      }),
  };

  // Reflection endpoints
  reflections = {
    list: (params?: {
      materialId?: string;
      userId?: string;
      status?: ReflectionStatus;
      page?: number;
      limit?: number;
    }) =>
      this.request<{ items: Reflection[]; meta: PaginationMeta }>('/api/v1/reflections', {
        params,
      }),

    get: (id: string) => this.request<Reflection>(`/api/v1/reflections/${id}`),

    submit: (data: {
      materialId?: string;
      programId?: string;
      userId: string;
      prompt: string;
      response: string;
      isShared?: boolean;
    }) => this.request<Reflection>('/api/v1/reflections', { method: 'POST', body: data }),

    review: (
      id: string,
      data: { status: ReflectionStatus; feedback?: string; reviewedBy: string }
    ) =>
      this.request<Reflection>(`/api/v1/reflections/${id}/review`, { method: 'PUT', body: data }),
  };

  // Result endpoints
  results = {
    list: (params?: { enrollmentId?: string; type?: string }) =>
      this.request<LearningResult[]>('/api/v1/results', { params }),

    getCompletion: (enrollmentId: string) =>
      this.request<{
        status: string;
        progress: number;
        requirements: {
          materials: { total: number; completed: number; isDone: boolean };
          evaluations: { total: number; passed: number; isDone: boolean };
          reflections: { isDone: boolean; status: string };
        };
        missingComponents: string[];
        nextSteps: string;
      }>(`/api/v1/results/enrollment/${enrollmentId}/completion`),
  };

  // WhatsApp Group endpoints
  whatsappGroups = {
    list: (params?: { gender?: string; skip?: number; take?: number; programId?: string }) =>
      this.request<(WhatsAppGroup & { members: WhatsAppGroupMember[] })[]>(
        '/api/v1/whatsapp-groups',
        { params }
      ),

    get: (id: string) =>
      this.request<WhatsAppGroup & { members: WhatsAppGroupMember[] }>(
        `/api/v1/whatsapp-groups/${id}`
      ),

    create: (data: Partial<WhatsAppGroup>) =>
      this.request<WhatsAppGroup>('/api/v1/whatsapp-groups', { method: 'POST', body: data }),

    update: (id: string, data: Partial<WhatsAppGroup>) =>
      this.request<WhatsAppGroup>(`/api/v1/whatsapp-groups/${id}`, { method: 'PUT', body: data }),

    delete: (id: string) =>
      this.request<void>(`/api/v1/whatsapp-groups/${id}`, { method: 'DELETE' }),

    addMember: (groupId: string, data: { phone: string; name: string }) =>
      this.request<WhatsAppGroupMember>(`/api/v1/whatsapp-groups/${groupId}/members`, {
        method: 'POST',
        body: data,
      }),

    removeMember: (groupId: string, phone: string) =>
      this.request<void>(`/api/v1/whatsapp-groups/${groupId}/members/${phone}`, {
        method: 'DELETE',
      }),
  };

  // User endpoints
  users = {
    list: (params?: {
      page?: number;
      limit?: number;
      search?: string;
      excludeProgramId?: string;
      programId?: string;
    }) => this.request<{ items: User[]; meta: PaginationMeta }>('/api/v1/users', { params }),

    get: (id: string) => this.request<User>(`/api/v1/users/${id}`),

    getDetails: (id: string) =>
      this.request<
        User & {
          legacyMetadata?: Record<string, unknown> | null;
          enrollments: {
            id: string;
            status: string;
            progress?: number;
            startedAt?: string;
            completedAt?: string;
            program: { id: string; name: string; code?: string };
          }[];
          registrations?: {
            id: string;
            status: string;
            domisili?: string;
            reason?: string;
            registeredAt: string;
            program: { id: string; name: string; code?: string };
            participantId?: { participantCode: string } | null;
          }[];
          auditLogs: { id: string; action: string; entity: string; createdAt: string }[];
        }
      >(`/api/v1/users/${id}/details`),

    create: (data: Partial<User>) =>
      this.request<User>('/api/v1/users', { method: 'POST', body: data }),

    update: (id: string, data: Partial<User>) =>
      this.request<User>(`/api/v1/users/${id}`, { method: 'PUT', body: data }),

    delete: (id: string) => this.request<void>(`/api/v1/users/${id}`, { method: 'DELETE' }),

    deactivate: (id: string) =>
      this.request<User>(`/api/v1/users/${id}/deactivate`, { method: 'PUT' }),

    activate: (id: string) => this.request<User>(`/api/v1/users/${id}/activate`, { method: 'PUT' }),

    recordPasswordResetRequest: (id: string) =>
      this.request<{ accepted: boolean }>(`/api/v1/users/${id}/password-reset`, {
        method: 'POST',
      }),

    listRoles: () => this.request<Role[]>('/api/v1/users/roles'),
  };

  // Academic Transcripts endpoints
  academicTranscripts = {
    list: (params?: { userId?: string; programId?: string }) =>
      this.request<AcademicTranscript[]>('/api/v1/academic-transcripts', { params }),

    getProgramTranscript: (programId: string, params?: { userId?: string }) =>
      this.request<ComprehensiveTranscript>(`/api/v1/academic-transcripts/program/${programId}`, {
        params,
      }),

    getProgressReport: (programId: string, params?: { userId?: string }) =>
      this.request<ComprehensiveTranscript>(`/api/v1/academic-transcripts/progress/${programId}`, {
        params,
      }),

    getLegacy: (params?: { userId?: string }) =>
      this.request<LegacyAcademicTranscriptResponse>('/api/v1/academic-transcripts/legacy', {
        params,
      }),
  };

  // Storage endpoints
  storage = {
    upload: (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      return this.upload<{ url: string }>('/api/v1/storage/upload', formData);
    },
  };

  // System and Utility endpoints
  system = {
    stats: () =>
      this.request<{
        uptime: number;
        status: string;
        ts: number;
      }>('/api/v1/health'),
    getHealth: () =>
      this.request<{
        uptime: number;
        status: string;
        ts: number;
      }>('/api/v1/health'),
  };

  // Announcement endpoints
  announcements = {
    list: (params?: {
      page?: number;
      limit?: number;
      search?: string;
      category?: AnnouncementCategory;
      priority?: AnnouncementPriority;
      targetAudience?: AnnouncementTarget;
      isActive?: boolean;
    }) =>
      this.request<{ items: Announcement[]; meta: PaginationMeta }>('/api/v1/announcements', {
        params,
      }),

    listActive: (params?: { targetAudience?: AnnouncementTarget; programId?: string }) =>
      this.request<Announcement[]>('/api/v1/announcements/active', { params }),

    get: (id: string) => this.request<Announcement>(`/api/v1/announcements/${id}`),

    create: (data: CreateAnnouncementInput) =>
      this.request<Announcement>('/api/v1/announcements', {
        method: 'POST',
        body: data,
      }),

    update: (id: string, data: UpdateAnnouncementInput) =>
      this.request<Announcement>(`/api/v1/announcements/${id}`, {
        method: 'PUT',
        body: data,
      }),

    delete: (id: string) => this.request<void>(`/api/v1/announcements/${id}`, { method: 'DELETE' }),

    toggleActive: (id: string) =>
      this.request<Announcement>(`/api/v1/announcements/${id}/toggle-active`, {
        method: 'PATCH',
      }),
  };

  // Mail / SMTP endpoints
  mail = {
    getStatus: () =>
      this.request<{
        status: 'CONNECTED' | 'DISCONNECTED' | 'ERROR';
        provider: string;
        details: {
          success: boolean;
          message: string;
          host?: string;
          port?: number;
          user?: string;
        };
      }>('/api/v1/mail/status'),

    sendTest: (data: {
      to: string;
      template:
        | 'RESET_PASSWORD'
        | 'NEW_MATERIAL'
        | 'PENDING_EVALUATION'
        | 'INACTIVITY_REMINDER'
        | 'REGISTRATION_APPROVED'
        | 'REGISTRATION_SUBMITTED';
      recipientName?: string;
    }) =>
      this.request<{
        success: boolean;
        message: string;
        messageId?: string;
        response?: string;
      }>('/api/v1/mail/test', {
        method: 'POST',
        body: data,
      }),
  };

  crm = {
    getParticipants: (params?: {
      inactivityType?: string;
      outreachStatus?: string;
      programId?: string;
      gender?: string;
      search?: string;
      page?: number;
      limit?: number;
    }) =>
      this.request<CrmParticipantsResponse>('/api/v1/crm/participants', {
        params: params as Record<string, string | number | boolean | undefined>,
      }),

    createOutreachLog: (data: CreateOutreachLogInput) =>
      this.request<ParticipantOutreachLog>('/api/v1/crm/outreach', {
        method: 'POST',
        body: data,
      }),

    sendQuickWhatsApp: (data: QuickWhatsAppInput) =>
      this.request<{
        waUrl: string;
        phone: string;
        message: string;
        outreachLog: ParticipantOutreachLog;
      }>('/api/v1/crm/outreach/quick-whatsapp', {
        method: 'POST',
        body: data,
      }),

    sendReminderEmail: (data: SendReminderEmailInput) =>
      this.request<{
        success: boolean;
        email: string;
        outreachLog: ParticipantOutreachLog;
      }>('/api/v1/crm/outreach/send-email', {
        method: 'POST',
        body: data,
      }),

    getOutreachHistory: (userId: string) =>
      this.request<ParticipantOutreachLog[]>(`/api/v1/crm/outreach/history/${userId}`),

    updateOutreachLog: (id: string, data: { status?: OutreachStatus; notes?: string }) =>
      this.request<ParticipantOutreachLog>(`/api/v1/crm/outreach/${id}`, {
        method: 'PATCH',
        body: data,
      }),
  };
}

/**
 * Create a new API client instance
 */
export function createApiClient(config: ApiClientConfig): ApiClient {
  return new ApiClient(config);
}

export { ApiError };

export type {
  AcademicTranscript,
  ComprehensiveTranscript,
  ProgramCohortReport,
  PaginationMeta,
  User,
  Role,
  Program,
  ProgramReport,
  ProgramLevel,
  LearningFlow,
  ProgramRegistration,
  Enrollment,
  Material,
  Evaluation,
  EvaluationQuestion,
  EvaluationAttempt,
  MaterialCompletion,
  Reflection,
  LearningResult,
  WhatsAppGroup,
  WhatsAppGroupMember,
  ReflectionStatus,
  RegistrationStatus,
  ParticipantId,
  RegistrationReceipt,
  MaterialMedia,
  AnnouncementTarget,
  CreateAnnouncementInput,
  UpdateAnnouncementInput,
  CrmParticipantsResponse,
  ParticipantOutreachLog,
  CreateOutreachLogInput,
  QuickWhatsAppInput,
  SendReminderEmailInput,
  OutreachStatus,
  OutreachChannel,
  OutreachType,
  CrmParticipantItem,
  CrmMetricsSummary,
};
